import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { SORT_NEWEST } from '../../config/configSearch';
import { getBanners, getPartners } from '../../util/api';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

// The Partner page's two banner kinds, as server/api/banners.js names them.
export const PLACEMENT_PARTNER_INTRO = 'partner-intro';
export const PLACEMENT_PARTNER = 'partner';
export const PLACEMENT_PARTNER_INVITE = 'partner-invite';

const LISTINGS_PER_PAGE = 48;

// The carousel on the overview shows the items an admin has picked in
// /admin/listings ("Fremhev på Butikker"); the server finds them (GET
// /api/partners, featuredListingIds). The most ids one listings query takes:
const MAX_FEATURED = 100;

/**
 * Which partner the page shows products for.
 *
 * - `/butikker/:partnerId` shows that partner, found by username or id. An
 *   id or name that is not a verified partner is a 404, never someone else's
 *   products.
 * - `/butikker` shows no single partner's products: it is the overview, with
 *   the partners and the hand-picked carousel. It is never a 404, so the
 *   homepage circle and the menu always lead somewhere.
 *
 * @param {Array<{ id: string, username: string|null }>} partners
 * @param {string} [partnerIdParam]
 * @returns {{ notFound: boolean, selected: Object|null }}
 */
export const selectPartner = (partners = [], partnerIdParam = null) => {
  if (!partnerIdParam) {
    return { notFound: false, selected: null };
  }
  const selected =
    partners.find(p => p.username === partnerIdParam) ||
    partners.find(p => p.id === partnerIdParam) ||
    null;
  return { notFound: !selected, selected };
};

/** The link parameter for a partner: the username when there is one. */
export const partnerParam = partner => partner?.username || partner?.id;

// The same "for sale" rule the search page uses: in stock, or a listing with
// no stock set at all.
const FOR_SALE = { minStock: 1, stockMode: 'match-undefined' };

/**
 * How many listings a partner has for sale right now: the total the API
 * reports for a one-item query, so it follows every new and sold item.
 */
const countForSale = (partnerId, sdk) =>
  sdk.listings
    .query({ authorId: partnerId, perPage: 1, ...FOR_SALE, 'fields.listing': ['title'] })
    .then(response => response.data.meta?.totalItems ?? null)
    .catch(() => null);

/**
 * Partner items for sale, newest first, for cards.
 *
 * @param {Object} which - `{ authorId }` for a partner's page, `{ ids }` for the carousel
 */
const fetchPartnerListings = (which, config, sdk, perPage = LISTINGS_PER_PAGE, page = 1) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;
  return sdk.listings.query({
    ...which,
    perPage,
    page,
    // Newest first, by when each went public, as on the search page.
    sort: SORT_NEWEST,
    // For sale only, like search: a sold item is out of stock.
    ...FOR_SALE,
    // The partner's logo (its profile picture) for the seller line on each card,
    // as on the search page, instead of the account holder's initials (Guro,
    // 14 October).
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  });
};

/**
 * Partners and the page's banners come from our own server (both read data
 * the public API cannot see); the selected partner's products come from the
 * public API like any listing query.
 */
export const loadPartnerPage = createAsyncThunk(
  'PartnerPage/load',
  async ({ partnerIdParam, page = 1, config }, { dispatch, extra: sdk, rejectWithValue }) => {
    try {
      const rootURL = config?.marketplaceRootURL;
      const [partnersResponse, bannersResponse] = await Promise.all([
        getPartners(rootURL),
        getBanners(rootURL, 'partner'),
      ]);
      const partners = partnersResponse?.partners || [];
      const banners = bannersResponse?.banners || [];
      const { notFound, selected } = selectPartner(partners, partnerIdParam);

      // Each entrance says how many items that partner has for sale. Asked
      // on every load, so the number is always the current one.
      const counts = notFound ? [] : await Promise.all(partners.map(p => countForSale(p.id, sdk)));
      const forSaleCounts = Object.fromEntries(partners.map((p, i) => [p.id, counts[i]]));

      let listingRefs = [];
      let featuredRefs = [];
      let pagination = null;
      if (selected) {
        // A partner's own page: everything they have for sale, newest first,
        // 48 to a page with page links like search (?page=2).
        const response = await fetchPartnerListings(
          { authorId: selected.id },
          config,
          sdk,
          LISTINGS_PER_PAGE,
          page
        );
        dispatch(addMarketplaceEntities(response));
        listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
        pagination = response.data.meta || null;
      } else if (!notFound) {
        // The overview: only the items an admin has picked for the carousel,
        // which the server finds among all of the partners' listings. One
        // query by id; a picked item that has sold is left out like in search.
        const featuredIds = (partnersResponse?.featuredListingIds || []).slice(0, MAX_FEATURED);
        if (featuredIds.length > 0) {
          const response = await fetchPartnerListings(
            { ids: featuredIds },
            config,
            sdk,
            MAX_FEATURED
          );
          dispatch(addMarketplaceEntities(response));
          featuredRefs = response.data.data.map(({ id, type }) => ({ id, type }));
        }
      }
      return {
        partners,
        banners,
        notFound,
        selectedPartnerId: selected?.id || null,
        listingRefs,
        featuredRefs,
        forSaleCounts,
        pagination,
      };
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

const partnerPageSlice = createSlice({
  name: 'PartnerPage',
  initialState: {
    partners: [],
    banners: [],
    selectedPartnerId: null,
    listingRefs: [],
    featuredRefs: [],
    forSaleCounts: {},
    pagination: null,
    notFound: false,
    loadInProgress: false,
    loadError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadPartnerPage.pending, state => {
        state.loadInProgress = true;
        state.loadError = null;
      })
      .addCase(loadPartnerPage.fulfilled, (state, action) => {
        return { ...state, ...action.payload, loadInProgress: false };
      })
      .addCase(loadPartnerPage.rejected, (state, action) => {
        state.loadInProgress = false;
        state.loadError = action.payload;
      });
  },
});

export default partnerPageSlice.reducer;

// The page of a partner's items to show, from ?page=; the first if absent or not a page number.
export const pageFromSearch = search => {
  const page = Number(parse(search || '').page);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

export const loadData = (params, search, config) => dispatch =>
  dispatch(
    loadPartnerPage({
      partnerIdParam: params?.partnerId || null,
      page: pageFromSearch(search),
      config,
    })
  );
